import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useComplianceScore, ComplianceScore } from './use-compliance-score';
import { usePlateauDetection, PlateauDetectionResult } from './use-plateau-detection';

export type AlertSeverity = 'critical' | 'warning' | 'info';
export type AlertCategory = 'compliance' | 'plateau' | 'engagement' | 'session';

export interface EngagementAlert {
  id: string;
  type: AlertSeverity;
  category: AlertCategory;
  title: string;
  description: string;
  actionLabel?: string;
  actionRoute?: string;
  metric?: number;
}

export interface EngagementAlertsResult {
  alerts: EngagementAlert[];
  criticalCount: number;
  warningCount: number;
  infoCount: number;
}

interface UseEngagementAlertsOptions {
  periodDays?: number;
}

export function useEngagementAlerts(
  clientId: string | undefined,
  options: UseEngagementAlertsOptions = {}
) {
  const { periodDays = 7 } = options;

  const { score: complianceScore, loading: complianceLoading } = useComplianceScore(clientId, { periodDays });
  const { result: plateauResult, loading: plateauLoading } = usePlateauDetection(clientId);

  const [additionalData, setAdditionalData] = useState<{
    lastActivityDate: Date | null;
    missedSessions: number;
    upcomingSessionsCount: number;
  } | null>(null);
  const [additionalLoading, setAdditionalLoading] = useState(true);

  // Load additional engagement data
  const loadAdditionalData = useCallback(async () => {
    if (!clientId) {
      setAdditionalData(null);
      setAdditionalLoading(false);
      return;
    }

    try {
      const now = new Date();
      const startDate = new Date();
      startDate.setDate(now.getDate() - periodDays);

      // Get last activity (workout or nutrition log)
      const [workoutResult, nutritionResult, sessionsResult] = await Promise.all([
        // Last workout
        supabase
          .from('workout_logs')
          .select('started_at')
          .eq('client_id', clientId)
          .order('started_at', { ascending: false })
          .limit(1),
        // Last nutrition log
        supabase
          .from('nutrition_daily_summaries')
          .select('summary_date')
          .eq('client_id', clientId)
          .gt('meal_count', 0)
          .order('summary_date', { ascending: false })
          .limit(1),
        // Missed sessions in period (scheduled but not completed or cancelled)
        supabase
          .from('sessions')
          .select('id, date, completed_at, cancelled')
          .eq('client_id', clientId)
          .gte('date', startDate.toISOString().split('T')[0])
          .lt('date', now.toISOString().split('T')[0]),
      ]);

      // Determine last activity date
      let lastActivityDate: Date | null = null;

      if (workoutResult.data?.[0]) {
        lastActivityDate = new Date(workoutResult.data[0].started_at);
      }

      if (nutritionResult.data?.[0]) {
        const nutritionDate = new Date(nutritionResult.data[0].summary_date);
        if (!lastActivityDate || nutritionDate > lastActivityDate) {
          lastActivityDate = nutritionDate;
        }
      }

      // Count missed sessions (past date, not completed, not cancelled)
      const sessions = sessionsResult.data || [];
      const missedSessions = sessions.filter(s =>
        !s.cancelled &&
        !s.completed_at &&
        new Date(s.date) < now
      ).length;

      // Count upcoming sessions (future sessions)
      const { data: upcomingSessions } = await supabase
        .from('sessions')
        .select('id')
        .eq('client_id', clientId)
        .gte('date', now.toISOString().split('T')[0])
        .eq('cancelled', false)
        .limit(10);

      setAdditionalData({
        lastActivityDate,
        missedSessions,
        upcomingSessionsCount: upcomingSessions?.length || 0,
      });
    } catch (err) {
      console.error('Error loading additional engagement data:', err);
    } finally {
      setAdditionalLoading(false);
    }
  }, [clientId, periodDays]);

  useEffect(() => {
    loadAdditionalData();
  }, [loadAdditionalData]);

  // Generate alerts from all data sources
  const generateAlerts = useCallback((): EngagementAlertsResult => {
    const alerts: EngagementAlert[] = [];

    // Compliance-based alerts
    if (complianceScore) {
      // Critical: Very low overall compliance
      if (complianceScore.overallScore < 40) {
        alerts.push({
          id: 'compliance-critical',
          type: 'critical',
          category: 'compliance',
          title: 'Very Low Compliance',
          description: `Overall compliance is only ${complianceScore.overallScore}% this ${periodDays === 7 ? 'week' : 'period'}. Immediate attention needed.`,
          metric: complianceScore.overallScore,
        });
      }
      // Warning: Low workout compliance
      else if (complianceScore.workoutCompliance < 70 && complianceScore.scheduledSessions > 0) {
        alerts.push({
          id: 'workout-compliance-low',
          type: 'warning',
          category: 'compliance',
          title: 'Low Workout Compliance',
          description: `Only ${complianceScore.completedSessions}/${complianceScore.scheduledSessions} scheduled workouts completed (${complianceScore.workoutCompliance}%)`,
          metric: complianceScore.workoutCompliance,
        });
      }

      // Warning: Low nutrition compliance
      if (complianceScore.nutritionCompliance < 70) {
        alerts.push({
          id: 'nutrition-compliance-low',
          type: 'warning',
          category: 'compliance',
          title: 'Low Nutrition Logging',
          description: `Meals logged on only ${complianceScore.daysWithMeals}/${complianceScore.totalDays} days (${complianceScore.nutritionCompliance}%)`,
          metric: complianceScore.nutritionCompliance,
        });
      }

      // Info: Declining trend
      if (complianceScore.trend === 'declining') {
        alerts.push({
          id: 'compliance-declining',
          type: 'info',
          category: 'compliance',
          title: 'Compliance Trending Down',
          description: 'Engagement has decreased compared to the previous period',
        });
      }
    }

    // Plateau-based alerts
    if (plateauResult && plateauResult.hasPlateaus) {
      const severePlateaus = plateauResult.plateauedExercises.filter(p => p.weeksSinceProgress >= 5);
      const moderatePlateaus = plateauResult.plateauedExercises.filter(
        p => p.weeksSinceProgress >= 3 && p.weeksSinceProgress < 5
      );

      if (severePlateaus.length > 0) {
        alerts.push({
          id: 'plateau-severe',
          type: 'warning',
          category: 'plateau',
          title: 'Long-Term Plateaus Detected',
          description: `${severePlateaus.length} exercise${severePlateaus.length > 1 ? 's have' : ' has'} not progressed in 5+ weeks`,
          metric: severePlateaus.length,
        });
      }

      if (moderatePlateaus.length > 0) {
        alerts.push({
          id: 'plateau-moderate',
          type: 'info',
          category: 'plateau',
          title: 'Exercises Stalling',
          description: `${moderatePlateaus.length} exercise${moderatePlateaus.length > 1 ? 's' : ''} showing no weight increase in 3-4 weeks`,
          metric: moderatePlateaus.length,
        });
      }
    }

    // Activity-based alerts
    if (additionalData) {
      // Critical: No activity in 14+ days
      if (additionalData.lastActivityDate) {
        const daysSinceActivity = Math.floor(
          (new Date().getTime() - additionalData.lastActivityDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        if (daysSinceActivity >= 14) {
          alerts.push({
            id: 'inactive-critical',
            type: 'critical',
            category: 'engagement',
            title: 'Client Inactive',
            description: `No workout or nutrition logging in ${daysSinceActivity} days`,
            metric: daysSinceActivity,
          });
        } else if (daysSinceActivity >= 7) {
          alerts.push({
            id: 'inactive-warning',
            type: 'warning',
            category: 'engagement',
            title: 'Reduced Activity',
            description: `Last activity was ${daysSinceActivity} days ago`,
            metric: daysSinceActivity,
          });
        }
      }

      // Warning: Missed sessions
      if (additionalData.missedSessions > 0) {
        alerts.push({
          id: 'missed-sessions',
          type: additionalData.missedSessions >= 3 ? 'warning' : 'info',
          category: 'session',
          title: 'Missed Sessions',
          description: `${additionalData.missedSessions} scheduled session${additionalData.missedSessions > 1 ? 's were' : ' was'} not completed`,
          metric: additionalData.missedSessions,
        });
      }

      // Info: No upcoming sessions
      if (additionalData.upcomingSessionsCount === 0) {
        alerts.push({
          id: 'no-upcoming-sessions',
          type: 'info',
          category: 'session',
          title: 'No Upcoming Sessions',
          description: 'Client has no sessions scheduled. Consider booking their next session.',
          actionLabel: 'Schedule Session',
        });
      }
    }

    // Sort by severity
    const severityOrder: Record<AlertSeverity, number> = {
      critical: 0,
      warning: 1,
      info: 2,
    };
    alerts.sort((a, b) => severityOrder[a.type] - severityOrder[b.type]);

    return {
      alerts,
      criticalCount: alerts.filter(a => a.type === 'critical').length,
      warningCount: alerts.filter(a => a.type === 'warning').length,
      infoCount: alerts.filter(a => a.type === 'info').length,
    };
  }, [complianceScore, plateauResult, additionalData, periodDays]);

  const loading = complianceLoading || plateauLoading || additionalLoading;
  const result = loading ? null : generateAlerts();

  return {
    result,
    loading,
    reload: () => {
      loadAdditionalData();
    },
  };
}
